import {AuthApi} from "../api/AuthApi";
import {ApiError} from "../api/http";

// The signed-in session: tokens in localStorage, shared by every tab. Access tokens last 5 minutes; each
// refresh replaces the refresh token too, so refreshes are serialised across tabs (Web Locks) and each one
// re-reads storage first: a tab that waited usually finds a token another tab already renewed.

const TOKENS_KEY = 'tsa-auth';
const DEVICE_KEY = 'tsa-device-id';
const LOCK_NAME = 'tsa-auth-refresh';
const EXPIRY_MARGIN_SECONDS = 30;

interface StoredTokens {
    accessToken: string;
    refreshToken: string;
}

export interface SessionUser {
    username: string;
    displayName: string;
    emailAddress: string;
    claims: string[];
}

type Listener = () => void;
const listeners = new Set<Listener>();

function read(): StoredTokens | null {
    try {
        const json = localStorage.getItem(TOKENS_KEY);
        return json ? JSON.parse(json) : null;
    } catch {
        return null;
    }
}

function write(tokens: StoredTokens | null): void {
    try {
        if (tokens) {
            localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
        } else {
            localStorage.removeItem(TOKENS_KEY);
        }
    } catch {
        // Storage unavailable (private mode): the session lasts only as long as this page.
        memoryTokens = tokens;
    }
    listeners.forEach(x => x());
}

let memoryTokens: StoredTokens | null = null;
const current = (): StoredTokens | null => read() ?? memoryTokens;

// Other tabs signing in or out.
window.addEventListener('storage', e => {
    if (e.key === TOKENS_KEY) {
        listeners.forEach(x => x());
    }
});

interface AccessTokenClaims {
    exp: number;
    payload: SessionUser;
}

function decode(accessToken: string): AccessTokenClaims | null {
    try {
        const part = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const bytes = Uint8Array.from(atob(part), c => c.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
    } catch {
        return null;
    }
}

function isFresh(accessToken: string): boolean {
    const claims = decode(accessToken);
    return !!claims && claims.exp - EXPIRY_MARGIN_SECONDS > Date.now() / 1000;
}

async function withRefreshLock<T>(action: () => Promise<T>): Promise<T> {
    if (navigator.locks) {
        return navigator.locks.request(LOCK_NAME, action);
    }
    return action();
}

let pendingRefresh: Promise<string | null> | null = null;

export const tokenStore = {
    subscribe(listener: Listener): () => void {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },

    // A random id for this browser, which the API folds into the login identity's fingerprint.
    deviceId(): string {
        try {
            let id = localStorage.getItem(DEVICE_KEY);
            if (!id) {
                id = crypto.randomUUID();
                localStorage.setItem(DEVICE_KEY, id);
            }
            return id;
        } catch {
            return 'no-storage';
        }
    },

    // Changes whenever the session does (for useSyncExternalStore).
    snapshot(): string | null {
        return current()?.accessToken ?? null;
    },

    user(): SessionUser | null {
        const tokens = current();
        const claims = tokens ? decode(tokens.accessToken) : null;
        return claims?.payload ?? null;
    },

    async login(username: string, password: string): Promise<void> {
        const result = await AuthApi.login(username, password, this.deviceId());
        write({ accessToken: result.accessToken, refreshToken: result.refreshToken });
    },

    async logout(): Promise<void> {
        const tokens = current();
        write(null);
        memoryTokens = null;
        if (tokens) {
            // Ends the login identity on the server; signing out locally doesn't depend on it.
            await AuthApi.logout(tokens.accessToken).catch(() => undefined);
        }
    },

    // An access token that's good for at least another 30 seconds, or null when signed out.
    async accessToken(): Promise<string | null> {
        const tokens = current();
        if (!tokens) {
            return null;
        }
        if (isFresh(tokens.accessToken)) {
            return tokens.accessToken;
        }
        return this.refresh(tokens.accessToken);
    },

    // Renews the session now, e.g. to pick up a changed email address.
    renew(): Promise<string | null> {
        return this.refresh(current()?.accessToken);
    },

    // Renews the session. staleAccessToken is the token found to be expired or refused: if storage
    // already holds a different one by the time the lock is held, another tab (or call) renewed it.
    refresh(staleAccessToken?: string): Promise<string | null> {
        pendingRefresh ??= withRefreshLock(async () => {
            const tokens = current();
            if (!tokens) {
                return null;
            }
            if (tokens.accessToken !== staleAccessToken && isFresh(tokens.accessToken)) {
                return tokens.accessToken;
            }
            try {
                const result = await AuthApi.refresh(tokens.refreshToken, this.deviceId());
                write({ accessToken: result.accessToken, refreshToken: result.refreshToken });
                return result.accessToken;
            } catch (err) {
                if (err instanceof ApiError && err.status !== 0 && err.status < 500) {
                    write(null); // the refresh token is no longer valid: signed out
                    return null;
                }
                throw err;
            }
        }).finally(() => {
            pendingRefresh = null;
        });
        return pendingRefresh;
    }
};
